import { type ReactElement, useId, useState } from 'react';
import clsx from 'clsx';
import Select, { type FilterOptionOption, type GroupBase, type MultiValue } from 'react-select';
import styles from './style.module.scss';
import MenuList from './menu-list';
import reactSelectStyles from './react-select-styles';

export interface ReactSelectOption {
  value: string;
  label: string;
}

type SearchMatchMode = 'contains' | 'startsWith';

interface ReactSelectProperties {
  readonly className?: string;
  readonly label: string;
  readonly placeholder?: string;
  readonly options: ReactSelectOption[];
  readonly selected: MultiValue<ReactSelectOption>;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
  readonly hideDropdownIndicator?: boolean;
  readonly openMenuOnClick?: boolean;
  readonly minimumSearchCharacters?: number;
  readonly searchMatchMode?: SearchMatchMode;
  readonly onChange: (selected: MultiValue<ReactSelectOption>) => void;
}

const selectComponents = {
  IndicatorSeparator: () => null,
  MenuList,
};

const ReactSelect = ({
  className,
  disabled,
  dataTestId,
  label,
  placeholder,
  options,
  selected,
  hideDropdownIndicator = false,
  openMenuOnClick = true,
  minimumSearchCharacters,
  searchMatchMode = 'contains',
  onChange,
}: ReactSelectProperties): ReactElement => {
  const id = useId();
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const hasMinimumSearchCharacters =
    minimumSearchCharacters === undefined || inputValue.trim().length >= minimumSearchCharacters;
  let menuIsOpen: boolean | undefined;

  if (minimumSearchCharacters !== undefined) {
    menuIsOpen = disabled ? false : isFocused && hasMinimumSearchCharacters;
  }

  const filterOption = (option: FilterOptionOption<ReactSelectOption>, rawInput: string): boolean => {
    const normalizedInput = rawInput.trim().toLocaleLowerCase();

    if (normalizedInput.length === 0) {
      return true;
    }

    const normalizedLabel = option.label.toLocaleLowerCase();

    if (searchMatchMode === 'startsWith') {
      return normalizedLabel.startsWith(normalizedInput);
    }

    return normalizedLabel.includes(normalizedInput);
  };

  const componentsOverride = hideDropdownIndicator
    ? {
        ...selectComponents,
        DropdownIndicator: () => null,
      }
    : selectComponents;

  return (
    <div className={clsx(styles.container, className)} data-testid={dataTestId}>
      <label htmlFor={id}>{label}</label>
      <Select<ReactSelectOption, true, GroupBase<ReactSelectOption>>
        isMulti
        isDisabled={disabled}
        options={options}
        value={selected}
        onChange={onChange}
        inputId={id}
        placeholder={placeholder}
        openMenuOnClick={openMenuOnClick}
        openMenuOnFocus={openMenuOnClick}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        inputValue={inputValue}
        onInputChange={(newValue: string) => {
          setInputValue(newValue);
          return newValue;
        }}
        menuIsOpen={menuIsOpen}
        filterOption={filterOption}
        components={componentsOverride}
        styles={reactSelectStyles}
      />
    </div>
  );
};

export default ReactSelect;
